import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import styles from "./Dialog.module.scss";

/**
 * Props for the DialogContent component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type DialogContentProps = ComponentPropsWithoutRef<"div">;

/**
 * DialogContent component for the main content area of a Dialog.
 *
 * Features:
 * - Main content container for dialog body
 * - Flexible height with overflow handling
 * - Consistent padding and spacing
 * - Responsive design
 * - Proper content scrolling for long content
 *
 * @example
 * <Dialog.Content>
 *   <p>This is the main content of the dialog.</p>
 * </Dialog.Content>
 */
export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  (props, ref) => {
    const { children, className, ...rest } = props;

    const contentClassNames = cx(styles["content"], className);

    return (
      <div
        className={contentClassNames}
        data-anv="dialog-content"
        ref={ref}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

DialogContent.displayName = "DialogContent";
